import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Flex, Text, Input, useToast, VStack, Divider, Spinner, Center, IconButton, Tooltip } from "@chakra-ui/react";
import axios from "axios";
import { MdOutlineFileDownload } from "react-icons/md";
import { FaArrowUpFromBracket, FaTrash } from "react-icons/fa6";

const validateFile = (file, toast) => {
  const allowedExtensions = ['.xlsx', '.csv', '.pdf'];
  const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

  if (!allowedExtensions.includes(fileExtension)) {
    toast({
      title: "Invalid file type",
      description: "Only .xlsx, .csv, and .pdf files are allowed",
      status: "error",
      duration: 3000
    });
    return false;
  }

  if (file.size > 20 * 1024 * 1024) {
    toast({
      title: "File too large",
      description: "Maximum file size is 20MB",
      status: "error",
      duration: 3000
    });
    return false;
  }

  return true;
};

const CustomerAdminFilesStep = ({ companyName }) => {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [companyData, setCompanyData] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState({});
  const [deleting, setDeleting] = useState({});

  const [uploadingLocation, setUploadingLocation] = useState(null);
  const [uploadingFileType, setUploadingFileType] = useState(null);
  const [uploadingCompanyFileType, setUploadingCompanyFileType] = useState(null);

  const locationFileInputRef = useRef();
  const companyAssetInputRef = useRef();
  const companyEnergyInputRef = useRef();

  const fetchData = async () => {
    setLoading(true);
    try {
      const config = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/asset-register/by-company/${encodeURIComponent(companyName)}`,
        config
      );
      setCompanyData(response.data);
    } catch (error) {
      console.error('Error loading company files:', error);
      toast({
        title: "Failed to load files",
        description: error.response?.data?.message || "An error occurred",
        status: "error",
        duration: 3000
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    if (companyName) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyName]);

  const handleCompanyUpload = async (e, fileType) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!validateFile(file, toast)) {
      e.target.value = null;
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("companyName", companyName);
    formData.append("fileType", fileType);

    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/asset-register/company/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast({ title: "File uploaded successfully", status: "success", duration: 2000 });
      await fetchData();
    } catch (error) {
      console.error('Company upload error:', error);
      toast({
        title: "Failed to upload file",
        description: error.response?.data?.message || "An error occurred",
        status: "error",
        duration: 3000
      });
    }
    setUploading(false);
    e.target.value = null;
    setUploadingCompanyFileType(null);
  };

  const handleLocationUpload = async (e, location, fileType) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!validateFile(file, toast)) {
      e.target.value = null;
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("location", location);
    formData.append("fileType", fileType);

    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/asset-register/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast({ title: "File uploaded successfully", status: "success", duration: 2000 });
      await fetchData();
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: "Failed to upload file",
        description: error.response?.data?.message || "An error occurred",
        status: "error",
        duration: 3000
      });
    }
    setUploading(false);
    e.target.value = null;
    setUploadingLocation(null);
    setUploadingFileType(null);
  };

  const downloadBlob = async (url, params, filenameFallback) => {
    const config = {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      responseType: 'blob',
      params
    };
    const response = await axios.get(url, config);

    let filename = filenameFallback;
    const contentDisposition = response.headers['content-disposition'];
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="(.+)"/);
      if (match) filename = match[1];
    }

    const blob = new Blob([response.data], { type: response.headers['content-type'] });
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  };

  const handleCompanyDownload = async (fileType, slot) => {
    const key = `company-${fileType}-${slot ?? ''}`;
    setDownloading(prev => ({ ...prev, [key]: true }));
    try {
      await downloadBlob(
        `${process.env.REACT_APP_BACKEND_URL}/api/asset-register/company/download`,
        { companyName, fileType, slot },
        `${fileType.toLowerCase().replace(' ', '-')}-company-${companyName}.xlsx`
      );
      toast({ title: "Download started", status: "success", duration: 2000 });
    } catch (error) {
      console.error('Company download error:', error);
      toast({
        title: "Failed to download file",
        description: error.response?.data?.message || "An error occurred",
        status: "error",
        duration: 3000
      });
    }
    setDownloading(prev => ({ ...prev, [key]: false }));
  };

  const handleLocationDownload = async (location, fileType, slot) => {
    const key = slot != null ? `${location}-${fileType}-${slot}` : `${location}-${fileType}`;
    setDownloading(prev => ({ ...prev, [key]: true }));
    try {
      const params = { location, fileType };
      if (slot != null) params.slot = slot;
      await downloadBlob(
        `${process.env.REACT_APP_BACKEND_URL}/api/asset-register/download`,
        params,
        `${fileType.toLowerCase().replace(' ', '-')}.xlsx`
      );
      toast({ title: "Download started", status: "success", duration: 2000 });
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: "Failed to download file",
        description: error.response?.data?.message || "An error occurred",
        status: "error",
        duration: 3000
      });
    }
    setDownloading(prev => ({ ...prev, [key]: false }));
  };

  const handleCompanyDelete = async (fileType, slot) => {
    const key = `company-${fileType}-${slot ?? ''}`;
    setDeleting(prev => ({ ...prev, [key]: true }));
    try {
      const config = {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        params: { companyName, fileType, ...(slot != null && { slot }) },
      };
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/asset-register/company`, config);
      toast({ title: "File deleted successfully", status: "success", duration: 2000 });
      await fetchData();
    } catch (error) {
      console.error('Company delete error:', error);
      toast({
        title: "Failed to delete file",
        description: error.response?.data?.message || "An error occurred",
        status: "error",
        duration: 3000
      });
    }
    setDeleting(prev => ({ ...prev, [key]: false }));
  };

  const handleLocationDelete = async (location, fileType, slot) => {
    const key = slot != null ? `${location}-${fileType}-${slot}` : `${location}-${fileType}`;
    setDeleting(prev => ({ ...prev, [key]: true }));
    try {
      const config = {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        ...(slot != null && { params: { slot } })
      };
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/asset-register/${encodeURIComponent(location)}/${encodeURIComponent(fileType)}`,
        config
      );
      toast({ title: "File deleted successfully", status: "success", duration: 2000 });
      await fetchData();
    } catch (error) {
      console.error('Delete error:', error);
      toast({
        title: "Failed to delete file",
        description: error.response?.data?.message || "An error occurred",
        status: "error",
        duration: 3000
      });
    }
    setDeleting(prev => ({ ...prev, [key]: false }));
  };

  if (loading) {
    return (
      <Center py={10}>
        <VStack spacing={3}>
          <Spinner size="lg" color="blue.500" thickness="4px" />
          <Text color="gray.500" fontSize="sm">Loading files…</Text>
        </VStack>
      </Center>
    );
  }

  const rawCompanyAR = companyData?.companyFiles?.['Asset Register'];
  const companyAssetFiles = Array.isArray(rawCompanyAR) ? rawCompanyAR : (rawCompanyAR ? [rawCompanyAR] : []);
  const rawCompanyER = companyData?.companyFiles?.['Energy Report'];
  const companyEnergyFiles = Array.isArray(rawCompanyER) ? rawCompanyER : (rawCompanyER ? [rawCompanyER] : []);
  const locationsList = companyData?.locations || [];

  return (
    <Box>
      <Text fontWeight="bold" fontSize="lg" mb={1}>{companyName}</Text>
      <Text mb={4} fontSize="xs" color="gray.400">
        Supported: .xlsx, .csv, .pdf · max 20 MB · Asset Register: up to 2 files · Energy Report: Unlimited
      </Text>

      <VStack spacing={0} align="stretch" divider={<Divider />} borderWidth="1px" borderColor="gray.300" borderRadius="lg" overflow="hidden">
        {/* Company-level files */}
        <Box px={4} py={3} bg="blue.50">
          <Text fontWeight="semibold" fontSize="sm" color="blue.700" mb={3}>Company Level</Text>
          <Flex gap={6} direction={{ base: "column", md: "row" }} align="flex-start">

            <Box flex={1} minW={0}>
              <Flex align="center" justify="space-between" mb={2} gap={2}>
                <Flex align="center" gap={2} minW={0}>
                  <Text fontSize="10px" fontWeight="bold" color="gray.500" textTransform="uppercase" letterSpacing="wider" flexShrink={0}>
                    Asset Register
                  </Text>
                  {companyAssetFiles.length >= 2 && (
                    <Text fontSize="xs" color="orange.500" fontStyle="italic">
                      Limit reached — delete one to upload a new file
                    </Text>
                  )}
                </Flex>
                {companyAssetFiles.length < 2 && (
                  <Tooltip label="Upload a company-level Asset Register file" hasArrow>
                    <Button
                      leftIcon={<FaArrowUpFromBracket size={10} />}
                      size="xs"
                      colorScheme="green"
                      variant="outline"
                      onClick={() => { setUploadingCompanyFileType('Asset Register'); companyAssetInputRef.current?.click(); }}
                      isLoading={uploading && uploadingCompanyFileType === 'Asset Register'}
                      loadingText="Uploading"
                    >
                      Upload
                    </Button>
                  </Tooltip>
                )}
              </Flex>
              {companyAssetFiles.length === 0 ? (
                <Text fontSize="xs" color="gray.400" fontStyle="italic">No file uploaded</Text>
              ) : (
                <VStack spacing={1} align="stretch">
                  {companyAssetFiles.map((file, idx) => {
                    const slot = file.slot != null ? file.slot : undefined;
                    const dlKey = `company-Asset Register-${slot ?? ''}`;
                    return (
                      <Flex key={file._id || idx} align="center" bg="white" borderRadius="md" px={2} py={1.5} gap={2}>
                        <Text fontSize="xs" flex={1} noOfLines={1} title={file.fileName}>{file.fileName}</Text>
                        <Text fontSize="xs" color="gray.400" whiteSpace="nowrap" flexShrink={0}>
                          {new Date(file.updatedAt).toLocaleDateString()}
                        </Text>
                        <Tooltip label="Download" hasArrow>
                          <IconButton
                            icon={<MdOutlineFileDownload size={15} />}
                            size="xs"
                            variant="ghost"
                            colorScheme="blue"
                            aria-label="Download"
                            onClick={() => handleCompanyDownload('Asset Register', slot)}
                            isLoading={downloading[dlKey]}
                          />
                        </Tooltip>
                        <Tooltip label="Delete" hasArrow>
                          <IconButton
                            icon={<FaTrash size={11} />}
                            size="xs"
                            variant="ghost"
                            colorScheme="red"
                            aria-label="Delete"
                            onClick={() => handleCompanyDelete('Asset Register', slot)}
                            isLoading={deleting[dlKey]}
                          />
                        </Tooltip>
                      </Flex>
                    );
                  })}
                </VStack>
              )}
            </Box>

            <Box w="1px" bg="gray.300" alignSelf="stretch" flexShrink={0} display={{ base: "none", md: "block" }} />

            <Box flex={1} minW={0}>
              <Flex align="center" justify="space-between" mb={2}>
                <Text fontSize="10px" fontWeight="bold" color="gray.500" textTransform="uppercase" letterSpacing="wider">
                  Energy Report
                </Text>
                <Tooltip label="Upload a company-level Energy Report" hasArrow>
                  <Button
                    leftIcon={<FaArrowUpFromBracket size={10} />}
                    size="xs"
                    colorScheme="green"
                    variant="outline"
                    onClick={() => { setUploadingCompanyFileType('Energy Report'); companyEnergyInputRef.current?.click(); }}
                    isLoading={uploading && uploadingCompanyFileType === 'Energy Report'}
                    loadingText="Uploading"
                  >
                    Upload
                  </Button>
                </Tooltip>
              </Flex>
              {companyEnergyFiles.length === 0 ? (
                <Text fontSize="xs" color="gray.400" fontStyle="italic">No file uploaded</Text>
              ) : (
                <VStack spacing={1} align="stretch">
                  {companyEnergyFiles.map((file, idx) => {
                    const slot = file.slot != null ? file.slot : undefined;
                    const dlKey = `company-Energy Report-${slot ?? ''}`;
                    return (
                      <Flex key={file._id || idx} align="center" bg="white" borderRadius="md" px={2} py={1.5} gap={2}>
                        <Text fontSize="xs" flex={1} noOfLines={1} title={file.fileName}>{file.fileName}</Text>
                        <Text fontSize="xs" color="gray.400" whiteSpace="nowrap" flexShrink={0}>
                          {new Date(file.updatedAt).toLocaleDateString()}
                        </Text>
                        <Tooltip label="Download" hasArrow>
                          <IconButton
                            icon={<MdOutlineFileDownload size={15} />}
                            size="xs"
                            variant="ghost"
                            colorScheme="blue"
                            aria-label="Download"
                            onClick={() => handleCompanyDownload('Energy Report', slot)}
                            isLoading={downloading[dlKey]}
                          />
                        </Tooltip>
                        <Tooltip label="Delete" hasArrow>
                          <IconButton
                            icon={<FaTrash size={11} />}
                            size="xs"
                            variant="ghost"
                            colorScheme="red"
                            aria-label="Delete"
                            onClick={() => handleCompanyDelete('Energy Report', slot)}
                            isLoading={deleting[dlKey]}
                          />
                        </Tooltip>
                      </Flex>
                    );
                  })}
                </VStack>
              )}
            </Box>
          </Flex>
        </Box>

        {/* Per-location files */}
        {locationsList.length === 0 ? (
          <Text color="gray.400" fontSize="sm" p={4}>No locations assigned to this admin.</Text>
        ) : (
          locationsList.map((locationData, locIndex) => {
            const locKey = locationData.locationDisplayName || locationData.location;
            const rawAR = locationData.files?.['Asset Register'];
            const assetFiles = Array.isArray(rawAR) ? rawAR : (rawAR ? [rawAR] : []);
            const rawER = locationData.files?.['Energy Report'];
            const energyFiles = Array.isArray(rawER) ? rawER : (rawER ? [rawER] : []);

            return (
              <Box key={locIndex} px={4} py={3}>
                <Text fontWeight="bold" fontSize="sm" color="gray.700" mb={3}>{locKey}</Text>
                <Flex gap={6} direction={{ base: "column", md: "row" }} align="flex-start">

                  <Box flex={1} minW={0}>
                    <Flex align="center" justify="space-between" mb={2} gap={2}>
                      <Flex align="center" gap={2} minW={0}>
                        <Text fontSize="10px" fontWeight="bold" color="gray.500" textTransform="uppercase" letterSpacing="wider" flexShrink={0}>
                          Asset Register
                        </Text>
                        {assetFiles.length >= 2 && (
                          <Text fontSize="xs" color="orange.500" fontStyle="italic">
                            Limit reached — delete one to upload a new file
                          </Text>
                        )}
                      </Flex>
                      {assetFiles.length < 2 && (
                        <Tooltip label="Upload a new Asset Register file" hasArrow>
                          <Button
                            leftIcon={<FaArrowUpFromBracket size={10} />}
                            size="xs"
                            colorScheme="green"
                            variant="outline"
                            onClick={() => { setUploadingLocation(locKey); setUploadingFileType('Asset Register'); locationFileInputRef.current?.click(); }}
                            isLoading={uploading && uploadingLocation === locKey && uploadingFileType === 'Asset Register'}
                            loadingText="Uploading"
                          >
                            Upload
                          </Button>
                        </Tooltip>
                      )}
                    </Flex>
                    {assetFiles.length === 0 ? (
                      <Text fontSize="xs" color="gray.400" fontStyle="italic">No file uploaded</Text>
                    ) : (
                      <VStack spacing={1} align="stretch">
                        {assetFiles.map((file, idx) => {
                          const slot = file?.slot != null && [1, 2].includes(file.slot) ? file.slot : undefined;
                          const dlKey = slot != null ? `${locKey}-Asset Register-${slot}` : `${locKey}-Asset Register`;
                          return (
                            <Flex key={file._id || idx} align="center" bg="gray.50" borderRadius="md" px={2} py={1.5} gap={2}>
                              <Text fontSize="xs" flex={1} noOfLines={1} title={file.fileName}>{file.fileName}</Text>
                              <Text fontSize="xs" color="gray.400" whiteSpace="nowrap" flexShrink={0}>
                                {new Date(file.updatedAt).toLocaleDateString()}
                              </Text>
                              <Tooltip label="Download" hasArrow>
                                <IconButton
                                  icon={<MdOutlineFileDownload size={15} />}
                                  size="xs"
                                  variant="ghost"
                                  colorScheme="blue"
                                  aria-label="Download"
                                  onClick={() => handleLocationDownload(locKey, 'Asset Register', slot)}
                                  isLoading={downloading[dlKey]}
                                />
                              </Tooltip>
                              <Tooltip label="Delete" hasArrow>
                                <IconButton
                                  icon={<FaTrash size={11} />}
                                  size="xs"
                                  variant="ghost"
                                  colorScheme="red"
                                  aria-label="Delete"
                                  onClick={() => handleLocationDelete(locKey, 'Asset Register', slot)}
                                  isLoading={deleting[dlKey]}
                                />
                              </Tooltip>
                            </Flex>
                          );
                        })}
                      </VStack>
                    )}
                  </Box>

                  <Box w="1px" bg="gray.300" alignSelf="stretch" flexShrink={0} display={{ base: "none", md: "block" }} />

                  <Box flex={1} minW={0}>
                    <Flex align="center" justify="space-between" mb={2}>
                      <Text fontSize="10px" fontWeight="bold" color="gray.500" textTransform="uppercase" letterSpacing="wider">
                        Energy Report
                      </Text>
                      <Tooltip label="Upload a new Energy Report" hasArrow>
                        <Button
                          leftIcon={<FaArrowUpFromBracket size={10} />}
                          size="xs"
                          colorScheme="green"
                          variant="outline"
                          onClick={() => { setUploadingLocation(locKey); setUploadingFileType('Energy Report'); locationFileInputRef.current?.click(); }}
                          isLoading={uploading && uploadingLocation === locKey && uploadingFileType === 'Energy Report'}
                          loadingText="Uploading"
                        >
                          Upload
                        </Button>
                      </Tooltip>
                    </Flex>
                    {energyFiles.length === 0 ? (
                      <Text fontSize="xs" color="gray.400" fontStyle="italic">No file uploaded</Text>
                    ) : (
                      <VStack spacing={1} align="stretch">
                        {energyFiles.map((file, idx) => {
                          const slot = file.slot != null ? file.slot : undefined;
                          const dlKey = slot != null ? `${locKey}-Energy Report-${slot}` : `${locKey}-Energy Report`;
                          return (
                            <Flex key={file._id || idx} align="center" bg="gray.50" borderRadius="md" px={2} py={1.5} gap={2}>
                              <Text fontSize="xs" flex={1} noOfLines={1} title={file.fileName}>{file.fileName}</Text>
                              <Text fontSize="xs" color="gray.400" whiteSpace="nowrap" flexShrink={0}>
                                {new Date(file.updatedAt).toLocaleDateString()}
                              </Text>
                              <Tooltip label="Download" hasArrow>
                                <IconButton
                                  icon={<MdOutlineFileDownload size={15} />}
                                  size="xs"
                                  variant="ghost"
                                  colorScheme="blue"
                                  aria-label="Download"
                                  onClick={() => handleLocationDownload(locKey, 'Energy Report', slot)}
                                  isLoading={downloading[dlKey]}
                                />
                              </Tooltip>
                              <Tooltip label="Delete" hasArrow>
                                <IconButton
                                  icon={<FaTrash size={11} />}
                                  size="xs"
                                  variant="ghost"
                                  colorScheme="red"
                                  aria-label="Delete"
                                  onClick={() => handleLocationDelete(locKey, 'Energy Report', slot)}
                                  isLoading={deleting[dlKey]}
                                />
                              </Tooltip>
                            </Flex>
                          );
                        })}
                      </VStack>
                    )}
                  </Box>
                </Flex>
              </Box>
            );
          })
        )}
      </VStack>

      <Input
        type="file"
        accept=".xlsx,.csv,.pdf"
        ref={locationFileInputRef}
        display="none"
        onChange={(e) => handleLocationUpload(e, uploadingLocation, uploadingFileType)}
      />
      <Input
        type="file"
        accept=".xlsx,.csv,.pdf"
        ref={companyAssetInputRef}
        display="none"
        onChange={(e) => handleCompanyUpload(e, 'Asset Register')}
      />
      <Input
        type="file"
        accept=".xlsx,.csv,.pdf"
        ref={companyEnergyInputRef}
        display="none"
        onChange={(e) => handleCompanyUpload(e, 'Energy Report')}
      />
    </Box>
  );
};

export default CustomerAdminFilesStep;
